import { Field, LinkField } from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import React from "react";
import * as Flex from "@repo/ui/flex";
import * as GridComponent from "@repo/ui/grid";
import * as Viewport from "@/components/viewport";
import { TYPOGRAPHY_LABEL3 } from "@/theme/topshop/typography/typography-constants";
import { ComponentProps } from "../../../../types/component-props";
import AnimatedCtaLink from "../../../animated-cta-link/animated-cta-link";
import { isMobile, appendRef } from "../../../utils";
import FeatureComponent, { withCtaText } from "../../feature";
import FeatureCarouselHeader from "../../feature-carousel-header";
import { Feature } from "../../types";
import styles from "./feature-grid.module.css";

export type FeatureGridProps = ComponentProps & {
  fields: {
    data: {
      datasource: {
        ctaRef: { jsonValue: Field<string> };
        ctaText: { jsonValue: Field<string> };
        ctaLink: { jsonValue: LinkField };
        header: { jsonValue: Field<string> };
        features: { results: Array<Feature> };
      };
    };
  };
};
const FeatureComponentWithCtaText = withCtaText(FeatureComponent);

const FeatureGrid = ({
  fields: {
    data: {
      datasource: {
        ctaLink: { jsonValue: ctaLink },
        ctaRef: { jsonValue: ctaRef },
        ctaText: { jsonValue: ctaText },
        header: { jsonValue: gridHeaderTitle },
        features: { results: features },
      },
    },
  },
}: FeatureGridProps) => {
  const mobileView = isMobile();
  const maxGridFeatures = mobileView ? 2 : 4;

  const headerLink = ctaLink.value.href ?? "";

  const hrefWithCtaRef = appendRef(headerLink, ctaRef.value);

  if (!features.length) {
    return null;
  }

  return (
    <div className={styles.gridContainer}>
      <FeatureCarouselHeader
        data-testid="carousel-header"
        title={gridHeaderTitle}
        link={{
          ...ctaLink,
          value: {
            ...ctaLink.value,
            href: hrefWithCtaRef,
            text: ctaText.value,
          },
        }}
      />
      <Flex.Container column gap="xl" justify="center" align="center">
        <GridComponent.Container
          columns={
            features.length <= maxGridFeatures
              ? features.length
              : maxGridFeatures
          }
          rowGap={mobileView ? "xl" : undefined}
        >
          {features
            .filter(
              (feature) =>
                feature.desktopImage.jsonValue.value.src &&
                feature.mobileImage.jsonValue.value.src,
            )
            .slice(0, 4)
            .map((feature, index) => (
              <GridComponent.Item
                className={styles.gridFeatureItem}
                key={index}
              >
                <FeatureComponentWithCtaText feature={feature} />
              </GridComponent.Item>
            ))}
        </GridComponent.Container>
        <Viewport.Small>
          <AnimatedCtaLink
            className={clsx(styles.ctaLink, TYPOGRAPHY_LABEL3)}
            link={{
              ...ctaLink,
              value: {
                ...ctaLink.value,
                text: ctaText.value,
              },
            }}
          />
        </Viewport.Small>
      </Flex.Container>
    </div>
  );
};

export default FeatureGrid;
